/**
 * Lottie files reach the player from several places, and only one of them is closed to
 * users: the bucket our designers publish to. A corpus scan of everything we host found
 * expressions in 1,649 designer files and in 4,498 icon assets, but the icon corpus is
 * where uploaded content lands -- including the working XSS payloads that motivated
 * LUM-649. Provenance is therefore what decides whether an expression is allowed to run
 * through lottie's `eval` or has to go through the sandbox.
 *
 * Deliberately not trusted here:
 * - `lumen5-user-*`, the per-workspace buckets that receive icon uploads
 * - `lumen5-prod-images`, where Freepik icons are ingested; users cannot write to it, but
 *   the content originates from a third party, so bucket trust is not provenance trust
 * - anything without a source url at all, which is the `lottieData` setter
 */
const TRUSTED_ORIGIN = 'https://storage.googleapis.com';
const TRUSTED_BUCKET = 'lumen5-prod-lottie';

/**
 * Whether a lottie source is one we publish ourselves.
 *
 * Parses rather than matches: a substring test would trust
 * `https://evil.example.com/?r=storage.googleapis.com/lumen5-prod-lottie/x.json`, and the
 * bucket has to be a whole path segment so a sibling like `lumen5-prod-lottie-staging`
 * does not inherit trust.
 */
export function isTrustedLottieSource(sourceUrl: string | null | undefined): boolean {
    if (!sourceUrl) {
        return false;
    }
    let parsed: URL;
    try {
        parsed = new URL(sourceUrl);
    } catch {
        return false;
    }
    if (parsed.origin !== TRUSTED_ORIGIN) {
        return false;
    }
    const [bucket] = parsed.pathname.replace(/^\//, '').split('/');
    return bucket === TRUSTED_BUCKET;
}
